/**
 * Mandala drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mandalaDrawingsPack: ToilePack = {
    slug: 'mandala-drawings',
    category: 'coloring',
    name: 'Mandala drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'simple', label: 'Simple mandala', subject: 'a simple mandala of four rings of petals around a small circle, every ring easy to draw with a compass and a pen'},
        {key: 'lotus', label: 'Lotus mandala', subject: 'a lotus mandala, layered pointed petals opening from the center, ringed by a band of small dots and leaves'},
        {key: 'detailed', label: 'Detailed mandala', subject: 'a detailed mandala of ten rings, each ring a different pattern of paisleys, scallops, teardrops and lattice'},
    ],
    styles: [
        {key: 'geometric', label: 'Compass and pen', style: 'crisp geometric black lines with an even stroke and plain concentric rings, as drawn with a compass', palette: 'Black ink only, no gray tones.', framing: 'The mandala centered on the page with a wide white margin.'},
        {key: 'tangle', label: 'Dense tangle', style: 'dense fine-line ink patterns, dots, scallops and tiny repeated shapes filling every ring', palette: 'Black ink only.', framing: 'The mandala centered on the page with a wide white margin.'},
        {key: 'coloring', label: 'For coloring', style: 'one bold black outline with large open cells, ready to color', palette: 'Black ink only, no fills, no shading.', framing: 'The mandala centered on the page with a wide white margin.'},
    ],
    directions: ['with moons', 'with leaves', 'with animals'],
    directionHint: 'try: with moons',
    seo: {
        title: 'Mandala Drawings: Simple, Lotus, Detailed Mandalas',
        metaDescription: 'Nine mandala drawings from simple to dense, drawn with a compass, in dense tangle ink or one bold outline for coloring.',
        h1: 'Mandala drawings: simple, lotus, detailed',
        tagline: 'Circular designs built in rings around a center, one mandala to a page.',
        intro: 'Mandala drawings are circular designs built in rings around a center, drawn for calm and for coloring, and taught as an art-class exercise. These nine pages show one mandala each, from a simple four-ring design to draw with a compass, to a lotus, to a dense ten-ring mandala. Each comes in crisp compass lines, dense fine-line tangle, or one bold outline. For a different center, type it in the direction box.',
        useCases: [
            'A calm coloring session',
            'A compass exercise in an art class',
            'A focus page for quiet time',
            'A symmetric pattern for a craft project',
        ],
        faq: [
            {q: 'Which mandala is easiest to start with?', a: 'The simple mandala has four rings of petals around a small circle, each ring drawn with a compass and a pen.'},
            {q: 'Does the coloring style have shading?', a: 'No. It is one bold black outline with open cells and no fills or shading.'},
            {q: 'Can I ask for a different mandala theme?', a: 'Yes. Type it in the direction box, such as with moons, with leaves or with animals. A typed theme is a request, so the result may differ from the samples.'},
        ],
    },
}
